import { Injectable, inject } from "@angular/core";
import { AlertService } from "@ui";
import { skipWhile, take } from "rxjs/operators";
import type { Corpus, DatePrecisionType } from "@pienso/ingest-types/corpus.interface";
import { TRANSFORM_TYPE, type Transform } from "@pienso/ingest-types/transform.interface";
import {
  ColumnTypesTransformDetails,
  CorpusDataService,
  type TransformDetails,
} from "../../services/corpus-data.service";
import { ES_TYPE } from "../../services/elastic-search.service";
import { AbstractTransformer } from "../abstract-transformer";
import { ToolbarItem } from "../toolbar-item";
import { TransformerGridDataService } from "../transformer-grid-data.service";
import { ColumnTypeService } from "./column-type.service";
import { ColumnTypeRendererProvider } from "./column-type-renderer.provider";
import { ColumnTypeToolbarComponent } from "./column-type-toolbar.component";
import { parseDateFormatToPrecision } from "./date-format.component";

@Injectable({ providedIn: "root" })
export class ColumnTypeTransformerService extends AbstractTransformer {
  protected override dataService: TransformerGridDataService;
  protected override corpusService: CorpusDataService;
  protected override alertService: AlertService;
  override service: ColumnTypeService;

  constructor() {
    const dataService = inject(TransformerGridDataService);
    const corpusService = inject(CorpusDataService);
    const alertService = inject(AlertService);
    const service = inject(ColumnTypeService);

    super(
      "columnTypes",
      "Column types",
      [TRANSFORM_TYPE.COLUMN_TYPES],
      corpusService,
      alertService,
      service,
      dataService,
      new ToolbarItem(ColumnTypeToolbarComponent),
      undefined,
      new ColumnTypeRendererProvider(),
    );

    this.dataService = dataService;
    this.corpusService = corpusService;
    this.alertService = alertService;
    this.service = service;
  }

  public describe(xform: Transform): string {
    switch (xform.transformType as TRANSFORM_TYPE) {
      case TRANSFORM_TYPE.COLUMN_TYPES:
        return `Changed column types`;
      default:
        throw new Error("Incompatible transform type for Column Types transform: " + xform.transformType);
    }
  }

  override async init(corpus: Corpus) {
    this.reset();
    const promise = super.init(corpus);
    this.dataService.gridData$
      .pipe(
        skipWhile((data) => !data),
        take(1),
      )
      .subscribe((data) => (this.service.columnTypes = data!.columnInfo.columnTypes));
    return promise;
  }

  protected createSavePayload(): TransformDetails {
    if (!this.service.columnTypes) {
      throw new Error("Column types are not set");
    }
    const payload = new ColumnTypesTransformDetails(this.service.columnTypes);
    const datePrecisions: { [colId: string]: DatePrecisionType } = {};
    Object.keys(this.service.columnTypes).forEach((colId: string) => {
      if (this.service.columnTypes![colId].type == ES_TYPE.DATE) {
        datePrecisions[colId] = parseDateFormatToPrecision(this.service.columnTypes![colId].format!);
      }
    });
    payload.details["datePrecisions"] = datePrecisions;
    return payload;
  }

  protected initFromTransform(xform: Transform) {
    if (xform.transformType !== TRANSFORM_TYPE.COLUMN_TYPES) {
      throw new Error("Incompatible transform details for Column Types transformer: " + xform.transformType);
    }
    const d = <any>xform.details;
    this.service.columnTypes = d.columns;
  }
}
